import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';

type DemoSpeaker = 'me' | 'prospect' | null;

interface DemoLine {
  speaker: DemoSpeaker;
  name: string;
  text: string;
  pauseAfter: number;
}

@Component({
  selector: 'app-qualify-demo',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './qualify-demo.component.html',
  styleUrls: ['./qualify-demo.component.scss'],
})
export class QualifyDemoComponent implements OnDestroy {
  @Input() title = 'What is Qualify and How does Qualify work?';
  @Input() subtitleItems: string[] = [
    'Add BANT, MEDDPICC, or custom qualification questions your team uses during sales calls.',
    'convverse joins your meeting automatically and captures prospect responses live.',
    'Prospect answers get mapped automatically to the relevant questions.',
    'Based on the response, convverse also shows follow-up questions to help you probe deeper.',
    'Helps you qualify stronger so deals dont slip later in your sales cycle.',
  ];
  @Input() autoStart = true;
  @Input() dismissOnBackdrop = true;

  show = false;
  isRunning = false;
  statusLabel = 'Ready to join';
  statusTone: 'ready' | 'live' | 'done' = 'ready';
  activeSpeaker: DemoSpeaker = null;
  captionName = 'Live Transcript';
  captionText = 'Waiting for participants to speak...';
  isTyping = false;
  questionAnswer = '';
  questionUpdated = false;

  private demoToken = 0;
  private readonly typingSpeed = 32;

  readonly meetingScript: DemoLine[] = [
    {
      speaker: 'me',
      name: 'You',
      text: 'Who will technically approve the product?',
      pauseAfter: 800,
    },
    {
      speaker: 'prospect',
      name: 'Prospect',
      text: 'The VP of Engineering will sign off on security, deployment, and data handling.',
      pauseAfter: 500,
    },
  ];

  open(): void {
    this.show = true;
    this.resetDemo();
    if (this.autoStart) {
      this.startDemo();
    }
  }

  close(): void {
    this.cancelDemo();
    this.show = false;
  }

  onBackdropClick(): void {
    if (this.dismissOnBackdrop) {
      this.close();
    }
  }

  startDemo(): void {
    if (this.isRunning) return;

    this.resetDemo();
    this.isRunning = true;
    this.statusTone = 'live';
    this.statusLabel = 'Live';

    const token = ++this.demoToken;
    void this.runMeeting(token);
  }

  ngOnDestroy(): void {
    this.cancelDemo();
  }

  private async runMeeting(token: number): Promise<void> {
    for (const line of this.meetingScript) {
      if (token !== this.demoToken) return;

      this.setActiveSpeaker(line.speaker);
      this.captionName = line.name;
      await this.typeWriter(line.text, token);
      if (token !== this.demoToken) return;

      await this.wait(line.pauseAfter);
    }

    if (token !== this.demoToken) return;

    this.setActiveSpeaker(null);
    this.applyAnswer(this.meetingScript[1].text);
    this.captionName = this.meetingScript[1].name;
    this.captionText = this.meetingScript[1].text;
    this.isRunning = false;
    this.statusTone = 'done';
    this.statusLabel = 'Meeting Ended';

    window.setTimeout(() => {
      if (!this.show || token !== this.demoToken) return;
      this.startDemo();
    }, 1800);
  }

  private resetDemo(): void {
    this.cancelDemo();
    this.statusTone = 'ready';
    this.statusLabel = 'Ready to join';
    this.activeSpeaker = null;
    this.captionName = 'Live Transcript';
    this.captionText = 'Waiting for participants to speak...';
    this.questionAnswer = '';
    this.questionUpdated = false;
    this.isTyping = false;
  }

  private cancelDemo(): void {
    this.demoToken += 1;
    this.isRunning = false;
    this.isTyping = false;
  }

  private async typeWriter(text: string, token: number): Promise<void> {
    this.captionText = '';
    this.isTyping = true;

    for (let i = 0; i < text.length; i += 1) {
      if (token !== this.demoToken) return;
      this.captionText += text.charAt(i);
      await this.wait(this.typingSpeed);
    }

    this.isTyping = false;
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }

  private setActiveSpeaker(speaker: DemoSpeaker): void {
    this.activeSpeaker = speaker;
  }

  private applyAnswer(answerText: string): void {
    this.questionUpdated = true;
    this.questionAnswer = answerText;
  }
}
